import { autoinject, bindable, Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { PubSub } from 'lib/event/PubSub';
import { AsyncErrorHandler, ComponentLoadingFader } from 'lib/ui';
import { MooringComponentType, MooringGridLine, MooringReviewDto, UpdateMooringGridLineDto } from 'models/mooring';
import { ToastService } from 'services';
import { MooringOfferService, MooringService } from 'services/mooring';
import { saveAs } from 'file-saver';
import { QueryFilter } from 'lib/tables/DataFilter';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { OfferViewContext } from '../../OfferViewContext';

@autoinject
export class GridLinesContainer {
  @bindable
  protected mooringId: number;

  @bindable
  protected reviewId?: number;
  protected review: MooringReviewDto;

  protected context = OfferViewContext.useContext(this.container);

  protected gridLines: MooringGridLine[] = [];

  constructor(
    private container: Container,
    protected element: Element,
    private mooringService: MooringService,
    private toaster: ToastService,
    private t: I18N,
    private pubsub: PubSub,
    private confirmation: ConfirmDialog,
    private mooringOfferService: MooringOfferService
  ) {
    pubsub.sub('filter-manager:filter-changed', (params: QueryFilter) => {
      void this.getGridLines(params);
    });
    pubsub.sub('entity:updated', async () => {
      void this.getGridLines();
    });
    pubsub.sub('list-entity:updated', async () => {
      void this.getGridLines();
    });
  }

  protected controlView: boolean;
  protected toggleControlView() {
    this.controlView = !this.controlView;
  }

  @AsyncErrorHandler
  protected async exportGridLines() {
    const blob = await this.mooringService.exportProductionGridLines(this.mooringId);
    saveAs(blob.blob, blob.fileName);
  }

  @AsyncErrorHandler
  @ComponentLoadingFader
  async attached() {
    if (!this.mooringId) {
      throw new Error('mooringId is required => mooring-id.bind="<id>"');
    }

    await this.getGridLines();
  }

  @AsyncErrorHandler
  protected async getGridLines(queryFilter?: QueryFilter) {
    const { data } = await this.mooringService.getGridLines(this.mooringId, queryFilter);
    const lines = data;
    if (lines.length) {
      for (const line of lines) {
        line.Components?.sort((a, b) => a.SortIndex - b.SortIndex);
      }
      setTimeout(() => {
        this.gridLines = lines;
      }, 10);
    }

    this.review = this.context.getState().offerReview;
  }

  @AsyncErrorHandler
  protected async onSaveGridLines({ entries, canNavigateOnSave }: { entries: MooringGridLine[]; canNavigateOnSave: boolean }) {
    const dto = entries.map(
      (line) =>
        ({
          Id: line.Id,
          Length: line.Length,
          DimForce: line.DimForce,
          MoorlogNo: line.MoorlogNo,
          IsAccident: line.IsAccident,
          StartBuoyId: line.StartBuoyId,
          EndBuoyId: line.EndBuoyId,
          AnalysisNo: line.AnalysisNo,
          SplicedLength: line.SplicedLength,
          MblRopes: line.MblRopes,
          MblSinkRopes: line.MblSinkRopes,
          MblStraps: line.MblStraps,
          MblChainShackleConnectionPlates: line.MblChainShackleConnectionPlates,
          MblUsedChainRopeWithKnot: line.MblUsedChainRopeWithKnot,
          SortIndex: line.SortIndex,
          //
          Components: line.Components,
        }) satisfies UpdateMooringGridLineDto
    );

    await this.mooringService.updateGridLines(this.mooringId, dto);
    this.toaster.showUpdated();
    this.pubsub.publish('list-entity:updated', { name: MooringComponentType.gridLine });
    if (canNavigateOnSave) {
      this.pubsub.publish('tabs:change-guard-ok', null);
    } else {
      await this.getGridLines();
    }
  }

  protected async setReviewStatus(reviewed: boolean) {
    const confirmed = await this.confirmation.confirmYesCancel(
      'dialog.areYouSure',
      reviewed
        ? this.t.tr('general.markAsCheckedConfirmationText', { x: this.t.tr('mooring.gridLine_plural').toLowerCase() })
        : this.t.tr('general.markAsUnCheckedConfirmationText', { x: this.t.tr('mooring.gridLine_plural').toLowerCase() })
    );

    if (!confirmed) return;

    await this.mooringOfferService.updateReviewStep(this.reviewId, { Step: 'GRIDLINES', Reviewed: reviewed });
    this.toaster.showSuccess(reviewed ? 'mooring.setAsChecked' : 'mooring.setAsNotChecked');
    this.pubsub.publish('entity-parent:updated', null);
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}
